/**
 * Web-specific Environment Configuration
 * On web, we need to access environment variables differently
 * Webpack will inject these at build time
 */

import Constants from 'expo-constants';

/**
 * Helper to read env on web:
 * 1) process.env (inlined at build if EXPO_PUBLIC_* or custom define)
 * 2) app.config.js -> expo.extra (Constants.expoConfig.extra)
 */
const get = (key: string): string => {
  // 1) Direct from process.env
  if (typeof process !== 'undefined' && process.env && process.env[key]) {
    return process.env[key] as string;
  }

  // 2) Fallback mapping to expo extra keys
  const extra = Constants?.expoConfig?.extra as Record<string, any> | undefined;
  if (extra) {
    const map: Record<string, string> = {
      SUPABASE_URL: 'supabaseUrl',
      SUPABASE_PUBLISHABLE_KEY: 'supabasePublishableKey',
      APP_ENV: 'appEnv',
      PERPLEXITY_API_KEY: 'perplexityApiKey',
      ALPHA_VANTAGE_API_KEY: 'alphaVantageApiKey',
      POLYGON_API_KEY: 'polygonApiKey',
    };
    const extraKey = map[key];
    if (extraKey && typeof extra[extraKey] === 'string') {
      return extra[extraKey] as string;
    }
  }

  return '';
};

export const ENV = {
  // Supabase (client-safe)
  SUPABASE_URL: get('SUPABASE_URL'),
  SUPABASE_PUBLISHABLE_KEY: get('SUPABASE_PUBLISHABLE_KEY'),

  // External API endpoints (no secrets)
  PERPLEXITY_API_URL: 'https://api.perplexity.ai',
  ALPHA_VANTAGE_API_URL: 'https://www.alphavantage.co/query',
  POLYGON_API_URL: 'https://api.polygon.io',

  // Optional keys (present in current code; avoid defaults in web bundle)
  PERPLEXITY_API_KEY: get('PERPLEXITY_API_KEY'),
  ALPHA_VANTAGE_API_KEY: get('ALPHA_VANTAGE_API_KEY'),
  POLYGON_API_KEY: get('POLYGON_API_KEY'),

  // App Config
  APP_ENV: get('APP_ENV') || 'production',
} as const;

/**
 * Diagnostic logging for development
 * Helps debug environment variable issues
 */
if (process.env.NODE_ENV === 'development') {
  console.log('🔍 ENV DIAGNOSTIC (Web):');
  console.log('Supabase URL:', ENV.SUPABASE_URL ? '✅ Set' : '❌ Missing');
  console.log('Supabase Key:', ENV.SUPABASE_PUBLISHABLE_KEY ? '✅ Set' : '❌ Missing');
  console.log('App Environment:', ENV.APP_ENV);
}

/**
 * Validates that all required environment variables are set
 * Returns validation result instead of throwing to prevent crashes
 */
export const validateEnv = (): { valid: boolean; missing: string[] } => {
  // Only enforce client-safe required values here
  const required: Array<keyof typeof ENV> = ['SUPABASE_URL', 'SUPABASE_PUBLISHABLE_KEY'];

  const missing = required.filter(key => !ENV[key]);

  if (missing.length > 0) {
    const message = `Missing required environment variables: ${missing.join(', ')}`;
    console.error(`❌ ${message}`);
  }

  return {
    valid: missing.length === 0,
    missing,
  };
};

// src/config/env.web.ts
